import { useLayoutEffect, type RefObject } from 'react';
import { heroGeometry, nextCompact, transitionClass } from '../lib/heroState';

/**
 * The floating page banner (prototype `setupHero`, final version).
 *
 * The wrapper is measured at full height and in its compact layout, then pinned (`position: sticky`) at a negative top
 * so that exactly the slim height stays visible under the header. When the page scrolls to that point the banner
 * switches to `compact` (with a 2px hysteresis). `--hdr` gets the measured header height and `--heroc` the slim
 * height (used by the sticky date rows). A re-render (`deps` change) restores the current state instantly (`instant`
 * class); the `slim-in` / `expand` animations only play on scroll-driven changes. Everything is recomputed on resize.
 *
 * The wrapper's `className` must stay constant in JSX: this hook owns its state classes.
 */
export function useFloatingBanner(ref: RefObject<HTMLElement | null>, deps: readonly unknown[]) {
  useLayoutEffect(() => {
    const hw = ref.current;
    if (!hw) return;
    const body = document.body;
    let cleanupScroll: (() => void) | null = null;
    let resizeTimer = 0;
    let exTimer = 0;
    let instantRaf = 0;

    const setup = () => {
      cleanupScroll?.();
      body.classList.remove('hero-compact');
      hw.style.cssText = 'position:static';
      hw.classList.remove('compact', 'slim-in', 'expand');
      const full = hw.offsetHeight;
      const docTop = hw.getBoundingClientRect().top + window.scrollY;
      hw.classList.add('compact', 'measure');
      const slim = (hw.querySelector('.hero') as HTMLElement | null)?.offsetHeight ?? 0;
      hw.classList.remove('compact', 'measure');
      const header = document.querySelector('.top') as HTMLElement | null;
      const hdr = header?.offsetHeight ?? 61;
      document.documentElement.style.setProperty('--hdr', `${hdr}px`);
      const { top, at } = heroGeometry(docTop, hdr, full, slim);
      hw.style.cssText = `height:${full}px;top:${top}px`;
      body.style.setProperty('--heroc', `${slim}px`);

      let on: boolean | null = null;
      let raf = 0;
      hw.classList.add('instant');
      const onScroll = () => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = 0;
          const want = nextCompact(on, window.scrollY, at);
          if (want === on) return;
          const was = on;
          on = want;
          hw.classList.toggle('compact', on);
          body.classList.toggle('hero-compact', on);
          const cls = transitionClass(was, on);
          if (!cls) return;
          hw.classList.remove('slim-in', 'expand');
          void hw.offsetWidth; // restart the animation
          hw.classList.add(cls);
          clearTimeout(exTimer);
          exTimer = window.setTimeout(() => hw.classList.remove(cls), 550);
        });
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
      cancelAnimationFrame(instantRaf);
      instantRaf = requestAnimationFrame(() => {
        instantRaf = requestAnimationFrame(() => hw.classList.remove('instant'));
      });
      cleanupScroll = () => {
        window.removeEventListener('scroll', onScroll);
        cancelAnimationFrame(raf);
      };
    };

    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(setup, 150);
    };

    setup();
    window.addEventListener('resize', onResize);
    return () => {
      cleanupScroll?.();
      window.removeEventListener('resize', onResize);
      clearTimeout(resizeTimer);
      clearTimeout(exTimer);
      cancelAnimationFrame(instantRaf);
      body.classList.remove('hero-compact');
    };
  }, deps);
}
